import { ApiError, type ProblemDetails } from './problem'

/**
 * The single way the application talks to the API (SPEC §13.4).
 *
 * Everything goes through here so that credentials, anti-forgery, correlation,
 * and problem-document handling are decided once rather than per call site.
 */

/** Echoed back by the server, and shown to the user when something fails. */
export const CorrelationHeader = 'X-Correlation-ID'

/** Anti-forgery header name (SPEC §14.8). */
export const AntiforgeryHeader = 'X-CSRF-TOKEN'

/** The build the server is running, compared against this client's (§13.1). */
export const BuildVersionHeader = 'X-Build-Version'

/** The problem type the server uses when the token is missing or stale. */
const AntiforgeryProblemType = 'https://gatheratmabels.com/problems/antiforgery'

let antiforgeryToken: string | null = null

/** Stores the anti-forgery token issued during SPA bootstrap. */
export function setAntiforgeryToken(token: string | null): void {
  antiforgeryToken = token
}

/** Shared so a burst of mutations on a cold start fetches one token, not five. */
let tokenInFlight: Promise<string | null> | null = null

/**
 * Fetches a token, or reuses the one already held.
 *
 * Tokens are bound to the caller's identity, so the one obtained while signed
 * out stops working the moment someone signs in. Rather than making every call
 * site remember that, a rejected request refreshes once and retries — see
 * `request` below.
 */
async function ensureAntiforgeryToken(force = false): Promise<string | null> {
  if (antiforgeryToken && !force) return antiforgeryToken
  if (tokenInFlight) return tokenInFlight

  tokenInFlight = (async () => {
    try {
      const response = await fetch('/api/v1/antiforgery/token', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })

      if (!response.ok) return null

      const body = (await response.json()) as { token: string }
      antiforgeryToken = body.token
      return antiforgeryToken
    } catch {
      // Offline, or the app is still waking up. The caller's own request will
      // fail next and report something the person can act on.
      return null
    } finally {
      tokenInFlight = null
    }
  })()

  return tokenInFlight
}

/** Called at startup so the first mutation does not pay for the round trip. */
export async function primeAntiforgeryToken(): Promise<void> {
  await ensureAntiforgeryToken()
}

/** Called when the server says the session is gone, so the app can react once. */
type UnauthenticatedHandler = () => void
let onUnauthenticated: UnauthenticatedHandler | null = null

export function setUnauthenticatedHandler(handler: UnauthenticatedHandler | null): void {
  onUnauthenticated = handler
}

/**
 * Called with the build the server reported, on every answer it gives.
 *
 * Every answer rather than a poll of its own: a client that has been sitting in
 * a service worker's cache finds out it is behind the moment it asks the server
 * for anything, which is the first thing it does (SPEC §13.1, MT-112).
 */
type BuildVersionHandler = (version: string | null) => void
let onBuildVersion: BuildVersionHandler | null = null

export function setBuildVersionHandler(handler: BuildVersionHandler | null): void {
  onBuildVersion = handler
}

interface RequestOptions {
  /** Aborts the request; pass a signal when a component may unmount mid-flight. */
  signal?: AbortSignal
  /** Extra headers, merged after the defaults. */
  headers?: Record<string, string>
}

const MutatingMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

async function request<TResponse>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  isRetry = false,
): Promise<TResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  }

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  // Only state-changing requests need it, and sending it on reads would leak
  // the token into more places than necessary.
  if (MutatingMethods.has(method)) {
    const token = await ensureAntiforgeryToken()
    if (token) headers[AntiforgeryHeader] = token
  }

  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Household and organizer auth are both cookie-based (SPEC §14.1), and
      // one origin means no CORS to negotiate (ADR-002).
      credentials: 'same-origin',
      signal: options.signal,
    })
  } catch (cause) {
    // An abort is the caller's own doing, not a failure to report.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause

    // Status 0: the request never reached a server. Offline, DNS, or the
    // Sunday-morning case where the app is still waking up.
    throw new ApiError(0, null, 'Network request failed')
  }

  // Before the status is looked at. A 401 or a 500 was still produced by a
  // build, and the one response that tells you the server moved on should not
  // be the one that happens to have failed.
  onBuildVersion?.(response.headers.get(BuildVersionHeader))

  if (response.status === 401) {
    onUnauthenticated?.()
  }

  if (!response.ok) {
    const problem = await readProblem(response)

    // A stale token is recoverable and invisible to the person using the app.
    // Signing in rebinds the token to the new identity, so the very first
    // action after sign-in would otherwise fail once, every time.
    if (response.status === 403 && problem?.type === AntiforgeryProblemType && !isRetry) {
      await ensureAntiforgeryToken(true)
      return request<TResponse>(method, path, body, options, true)
    }

    throw new ApiError(response.status, problem, `HTTP ${response.status}`)
  }

  if (response.status === 204) {
    return undefined as TResponse
  }

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as TResponse
}

/**
 * Reads a problem document, tolerating responses that are not one.
 *
 * A proxy, a gateway, or a crash can return HTML where the contract promises
 * problem+json. Parsing must not throw and replace a useful status code with a
 * JSON syntax error.
 */
async function readProblem(response: Response): Promise<ProblemDetails | null> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) return null

  try {
    return (await response.json()) as ProblemDetails
  } catch {
    return null
  }
}

export const api = {
  get: <TResponse>(path: string, options?: RequestOptions) =>
    request<TResponse>('GET', path, undefined, options),

  post: <TResponse>(path: string, body?: unknown, options?: RequestOptions) =>
    request<TResponse>('POST', path, body, options),

  put: <TResponse>(path: string, body?: unknown, options?: RequestOptions) =>
    request<TResponse>('PUT', path, body, options),

  delete: <TResponse>(path: string, options?: RequestOptions) =>
    request<TResponse>('DELETE', path, undefined, options),
}
